import { getAuth0Client } from "@/auth0";
import { clearStoredAuth0Subject } from "@/utils/authIdentityConsistency";
import { clearLegacyAuthState } from "@/utils/authState";
import {
  getLogoutMarkerCookieTimestamp,
  SESSION_STARTED_AT_STORAGE_KEY,
  shouldApplyLogoutMarker,
  writeLogoutMarkerCookie,
} from "@/utils/sharedLogoutContract";

function getAuthSessionStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function parseStoredTimestamp(value: string | null | undefined): number {
  if (!value) {
    return 0;
  }

  const timestamp = Number(value);

  return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : 0;
}

function readStoredSessionStartedAt(storage: Storage | null): number {
  try {
    return parseStoredTimestamp(storage?.getItem(SESSION_STARTED_AT_STORAGE_KEY));
  } catch {
    return 0;
  }
}

function removeStoredSessionStartedAt(storage: Storage | null): void {
  try {
    storage?.removeItem(SESSION_STARTED_AT_STORAGE_KEY);
  } catch {
    // Session tracking is best-effort; auth state can still be cleared locally.
  }
}

function storeSessionStartedAt(storage: Storage | null, sessionStartedAt: number): void {
  try {
    storage?.setItem(SESSION_STARTED_AT_STORAGE_KEY, String(sessionStartedAt));
  } catch {
    // Session tracking is best-effort; Auth0 remains the source of truth.
  }
}

export class AuthSessionManager {
  private logoutInProgress = false;

  public startAuthenticatedSessionTracking(): void {
    storeSessionStartedAt(getAuthSessionStorage(), Date.now());
  }

  public clearAuthenticatedSessionTracking(): void {
    removeStoredSessionStartedAt(getAuthSessionStorage());
  }

  public async handleManualLogout(): Promise<void> {
    if (this.logoutInProgress) {
      return;
    }
    this.logoutInProgress = true;

    try {
      writeLogoutMarkerCookie();

      // Defer clearing local auth state until we actually redirect: the access
      // token in the store must stay valid until then, otherwise concurrent
      // in-flight requests fire token-less and their 401 handlers race (and abort)
      // this federated logout, leaving the Auth0 session alive and looping login.
      await getAuth0Client().logout({
        logoutParams: { returnTo: `${globalThis.location.origin}/` },
        openUrl: (url: string) => {
          this.clearLocalAuthState();
          globalThis.location.replace(url);
        },
      });
    } finally {
      this.logoutInProgress = false;
    }
  }

  public async applySharedLogoutMarkerIfNeeded(): Promise<boolean> {
    if (!this.hasActiveSharedLogoutMarker()) {
      return false;
    }

    this.clearLocalAuthState();
    await this.clearAuth0LocalSession();
    return true;
  }

  public async handleUnauthorizedApiResponse(): Promise<void> {
    this.clearLocalAuthState();
    await this.clearAuth0LocalSession();
  }

  private async clearAuth0LocalSession(): Promise<void> {
    try {
      await getAuth0Client().logout({ openUrl: false });
    } catch {
      // Local legacy state has already been cleared, so Auth0 cache clearing is best-effort here.
    }
  }

  private clearLocalAuthState(): void {
    clearStoredAuth0Subject();
    clearLegacyAuthState();
    this.clearAuthenticatedSessionTracking();
  }

  private getStoredSessionStartedAt(): number {
    return readStoredSessionStartedAt(getAuthSessionStorage());
  }

  private hasActiveSharedLogoutMarker(): boolean {
    return shouldApplyLogoutMarker(getLogoutMarkerCookieTimestamp(), this.getStoredSessionStartedAt());
  }
}

let authSessionManager: AuthSessionManager | undefined;

export function getAuthSessionManager(): AuthSessionManager {
  authSessionManager ??= new AuthSessionManager();
  return authSessionManager;
}
